import { ParsedUrlQuery } from 'querystring';

import { ReactElement, useMemo, memo } from 'react';

import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';

import { useDevice } from '@contexts/device';
import { Star } from '@assets/icons';
import CustomIcon from '@components/utils/CustomIcon';
import { SliderImage } from '@components/ui/listing/NextListingItemTourSlider';
import { paramsToObject } from '@components/views/utils';

import { ListingItemProps } from './ListingItemInterface';

const DesktopListingItem = dynamic(() => import('./DesktopListingItem'));
const MobileListingItem = dynamic(() => import('./MobileListingItem'));

const ListingItem = ({
  fallBackImage,
  name,
  pricing: price,
  highlights,
  location,
  refundable,
  boardType,
  boardTypes,
  peopleCount,
  adultCount,
  childCount,
  checkInDate,
  checkOutDate,
  expedia,
  url,
  score,
  suggests,
  openDatePicker,
  installments,
  onGoToHotelDetailClick,
  seturExecutive,
  images,
  isDate,
  badge = [],
  reviews,
  activeKey,
  image,
  id,
  domestic,
  themes,
  status,
  selectedDepatureFlight,
  selectedDepatureTransfer,
  selectedReturnFlight,
  selectedReturnTransfer,
  ratings,
}: ListingItemProps) => {
  const { isDesktop } = useDevice();
  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);

  const entryDateMissing = !query.in;
  const link = encodeURIComponent(`${process.env.NEXT_PUBLIC_HOST}/${url}`);

  const handleDateSelection = () => {
    setTimeout(() => {
      openDatePicker?.();
    }, 200);
  };

  const onGoToHotelDetail = () => {
    console.log('onGoToHotelDetailClick');
    onGoToHotelDetailClick?.();
    if (!query.in && !query.out) {
      window.open(
        `${process.env.NEXT_PUBLIC_HOST}${url}&isPackage=true&fromListing=true&arr=ECN`,
        '_blank',
        'noopener, noreferrer'
      );
    } else if (query.dep) {
      window.open(
        `${process.env.NEXT_PUBLIC_HOST}${url}&isPackage=true&fromListing=true&arr=ECN&dep=${query.dep}&depIsCity=${query.depIsCity}&inboundFlightNo=${selectedReturnFlight?.segments[0].flightno}&outboundFlightNo=${selectedDepatureFlight?.segments[0].flightno}&lastFlightDate=${selectedReturnFlight?.departureDate}`,
        '_blank',
        'noopener, noreferrer'
      );
    } else {
      window.open(
        `${process.env.NEXT_PUBLIC_HOST}/${url}&isPackage=true&fromListing=true&arr=ECN`,
        '_blank',
        'noopener, noreferrer'
      );
    }
  };

  // ======================================
  // Generate the hotel card slider images
  // ======================================
  const itemImages = useMemo(() => {
    const items: SliderImage[] = [];

    images?.forEach((item) =>
      items.push({
        imageUrl: item.url || '',
        alt: item.hotelImageCategoryName || 'setur',
      })
    );

    return items;
  }, [images]);

  const getStars = useMemo(() => {
    const stars: Array<ReactElement> = [];
    if (score) {
      for (let index = 0; index < score; index++) {
        stars.push(
          <CustomIcon key={'name' + index} icon={Star} fontSize={12} />
        );
      }
    }
    return stars;
  }, [score]);

  const originalPrice =
    price?.rate && price?.rate > 0 ? price?.original : undefined;

  return isDesktop ? (
    <DesktopListingItem
      reviews={reviews}
      badge={badge}
      activeKey={activeKey}
      boardTypes={boardTypes}
      isDate={isDate}
      hotelListing={true}
      seturExecutive={seturExecutive}
      image={image}
      adultCount={adultCount}
      boardType={boardType}
      checkInDate={checkInDate}
      checkOutDate={checkOutDate}
      childCount={childCount}
      domestic={domestic}
      expedia={expedia}
      fallBackImage={fallBackImage}
      features={[]}
      highlights={highlights}
      id={id}
      location={location}
      name={name}
      openDatePicker={openDatePicker}
      pricing={price}
      refundable={refundable}
      score={score}
      status={status}
      suggests={suggests}
      themes={themes}
      url={url}
      installments={installments}
      onGoToHotelDetailClick={onGoToHotelDetailClick}
      images={images}
      peopleCount={peopleCount}
      entryDateMissing={entryDateMissing}
      link={link}
      handleDateSelection={handleDateSelection}
      onGoToHotelDetail={onGoToHotelDetail}
      itemImages={itemImages}
      originalPrice={originalPrice}
      getStars={getStars}
      selectedDepatureFlight={selectedDepatureFlight}
      selectedReturnFlight={selectedReturnFlight}
      selectedDepatureTransfer={selectedDepatureTransfer}
      selectedReturnTransfer={selectedReturnTransfer}
      ratings={ratings}
    />
  ) : (
    <MobileListingItem
      reviews={reviews}
      badge={badge}
      activeKey={activeKey}
      boardTypes={boardTypes}
      isDate={isDate}
      hotelListing={true}
      seturExecutive={seturExecutive}
      image={image}
      adultCount={adultCount}
      boardType={boardType}
      checkInDate={checkInDate}
      checkOutDate={checkOutDate}
      childCount={childCount}
      domestic={domestic}
      expedia={expedia}
      fallBackImage={fallBackImage}
      features={[]}
      highlights={highlights}
      id={id}
      location={location}
      name={name}
      openDatePicker={openDatePicker}
      pricing={price}
      refundable={refundable}
      score={score}
      status={status}
      suggests={suggests}
      themes={themes}
      url={url}
      installments={installments}
      onGoToHotelDetailClick={onGoToHotelDetailClick}
      images={images}
      peopleCount={peopleCount}
      entryDateMissing={entryDateMissing}
      link={link}
      handleDateSelection={handleDateSelection}
      onGoToHotelDetail={onGoToHotelDetail}
      itemImages={itemImages}
      originalPrice={originalPrice}
      getStars={getStars}
      selectedDepatureFlight={selectedDepatureFlight}
      selectedReturnFlight={selectedReturnFlight}
      selectedDepatureTransfer={selectedDepatureTransfer}
      selectedReturnTransfer={selectedReturnTransfer}
      ratings={ratings}
    />
  );
};

export default memo(ListingItem);
